import React, { useState } from 'react';
import {
    notifyErrorCustom,
    notifySuccessCustom,
} from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomProjectEntity } from '@entities/CustomProject/Types';
import { FormatDate } from '@shared/Helpers/FormatValue/FormatDate/FormatDate';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { DeleteProjectWarningModal } from '@modules/BillOfMaterials/Shared/Widgets/Modals/DeleteProjectWarningModal/DeleteProjectWarningModal';
import css from './DesktopProjectTableRow.module.css';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { ROUTES } from '@configs/AppRoutes';
import DeleteButton from '@shared/Ui/Buttons/DeleteButton/DeleteButton';
import { useProjectsListViewModel } from '@viewModels/ProjectsListViewModel/ProjectsListViewModel';

interface TableRowProjectProps {
    project: CustomProjectEntity;
}

export const DesktopProjectTableRow: React.FC<TableRowProjectProps> = ({
    project,
}) => {
    const { deleteCustomProject, error, fetchCustomProjectsList } =
        useProjectsListViewModel();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const handleOpenModal = () => setOpenDeleteModal(true);
    const handleCloseModal = () => setOpenDeleteModal(false);

    const handleDeleteProject = async () => {
        const currentPage = Number(searchParams.get('page')) || 1;

        const result = await deleteCustomProject(project.id);
        if (result.error) {
            notifyErrorCustom(result.message ?? 'Failed to delete project');
            return;
        }

        notifySuccessCustom('The project has been deleted successfully.');

        await fetchCustomProjectsList(currentPage);

        const { customProjects } = useProjectsListViewModel.getState();

        if (customProjects.pagination.total === 0) {
            navigate(ROUTES.HOME);
            return;
        }

        const maxPage = Math.ceil(
            customProjects.pagination.total / customProjects.pagination.perPage
        );

        if (currentPage > maxPage) {
            const newPage = Math.max(1, maxPage);
            searchParams.set('page', newPage.toString());
            setSearchParams(searchParams);

            await fetchCustomProjectsList(newPage);
        }

        handleCloseModal();
    };

    const handleViewProject = () => {
        navigate(
            ROUTES.STEP1.getPath(StepsViewModeEnum.PROJECT, String(project.id))
        );
    };

    return (
        <div className={css.row}>
            <div className={css.title}>{project.title}</div>
            <div className={css.date}>
                {project.createdAt
                    ? FormatDate(project.createdAt, {
                          format: 'DMY',
                          separator: '.',
                      })
                    : 'No date'}
            </div>
            <div className={css.dateTarget}>
                {project.targetDate
                    ? FormatDate(project.targetDate, {
                          format: 'DMY',
                          separator: '.',
                      })
                    : 'No date'}
            </div>
            <div className={css.address}>
                {project.address?.fullAddress ?? 'No address'}
            </div>
            <div className={css.actions}>
                <BasicButton
                    onClick={handleViewProject}
                    extraClassName={css.viewBtn}
                >
                    View Project
                </BasicButton>

                <DeleteButton
                    onClick={handleOpenModal}
                    extraClassName={css.deleteBtn}
                />
            </div>
            {openDeleteModal && (
                <DeleteProjectWarningModal
                    projectTitle={project.title}
                    onConfirm={handleDeleteProject}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};
